/**
 * useFunChallengesSocket
 *
 * Low-level envelope hook for the Fun Challenges feature.
 *
 * - Registers schemapack handlers for every `fun_*` message type once per
 *   socket. De-duped via a unique `handlerKey` so calling this hook from
 *   multiple components doesn't double-fire.
 * - Decodes each inbound `payload` (JSON string) and dispatches into the
 *   FunChallenges Zustand stores.
 * - Exposes a `send(type, gameType, id, payloadObj, requestId?)` helper that
 *   wraps `gameClient.send_msg` with the `funChallengeEnvelopeSchema` shape.
 *
 * The hook intentionally does NOT touch existing chess message
 * registrations on the same `gameClient` — only adds new types using a
 * separate schema (`funChallengeEnvelopeSchema`).
 *
 * Mount once near the top of the app tree (e.g. inside the global
 * `FunChallengesGlobalListener` from Phase 4) so `fun_room_invite` is
 * received regardless of the current screen.
 */

import {useContext, useEffect, useMemo, useRef} from 'react';
import {create} from 'zustand';

import {SocketContext} from '@/Providers/SocketProvider';
import useFunChallengesBattleStore from '@/Store/funChallengesBattleStore';
import useFunChallengesSessionStore from '@/Store/funChallengesSessionStore';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {
  BattleType,
  type ChallengeSettings,
  type FunChallengeError,
  type FunChallengeEnvelope,
  type FunMoveBroadcastPayload,
  type FunRoomUpdatePayload,
  type FunSessionRejoinPayload,
  type FunSessionStartPayload,
  type IncomingChallengePayload,
} from '@/Screens/FunChallenges/types';

const {schema} = require('../../../CCClientSdk');

/**
 * Shared registration signal. Tracks the socket identity that fun_* schemas
 * are currently registered against. Stored in a Zustand singleton so EVERY
 * `useFunChallengesSocket` consumer (global listener, lobby, battle screen)
 * subscribes to the same value. Without this, each consumer kept its own
 * `useState` copy and the lobby could remount with a stale `ready: false`
 * after a battle even though registration was actually live.
 */
interface FunRegistryState {
  registeredSocket: any | null;
  setRegisteredSocket: (s: any) => void;
}

const useFunChallengesRegistryStore = create<FunRegistryState>(set => ({
  registeredSocket: null,
  setRegisteredSocket: s => set({registeredSocket: s}),
}));

const HANDLER_PREFIX = 'FunChallenges:';

/**
 * Stable requestId we tag onto the speculative `fun_session_rejoin` we send
 * on every new socket. If the server replies with a NO_ACTIVE_SESSION /
 * SESSION_* error against this requestId, we treat it as "nothing to
 * rejoin" rather than a user-visible error.
 */
const REJOIN_PROBE_REQUEST_ID = 'fun-rejoin-probe';

const FUN_INBOUND_TYPES = [
  'fun_room_created',
  'fun_room_invite',
  'fun_room_update',
  'fun_session_start',
  'fun_move_broadcast',
  'fun_session_end',
  'fun_session_rejoin',
  'fun_error',
] as const;

/**
 * Outbound (client → server) types. The SDK's `send_msg` checks whether the
 * event has a registered schema in `schemaTypeDict`; if not, the payload is
 * emitted as a plain JS object and the server's schemapack decoder rejects
 * it silently. We MUST register these schemas before any send.
 */
const FUN_OUTBOUND_TYPES = [
  'fun_room_create',
  'fun_room_join',
  'fun_room_leave',
  'fun_room_start',
  'fun_session_rejoin',
  'fun_move',
  'fun_forfeit',
] as const;

function parsePayload<T = any>(raw: any): T | null {
  if (!raw) return null;
  if (typeof raw !== 'string') return raw as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function asBattleType(s: string | undefined): BattleType {
  return (s as BattleType) || BattleType.SQUARE_TRAINER;
}

interface SendArgs {
  type: string;
  gameType: BattleType | string;
  id?: string;
  payload?: Record<string, any>;
  requestId?: string;
}

export interface FunChallengesSocketApi {
  /** Underlying socket — null until SocketProvider has connected. */
  socket: any | null;
  /** True iff the socket exists AND fun_* registrations are in place. */
  ready: boolean;
  /** Send a fun_* envelope. No-op (returns false) if the socket isn't ready. */
  send: (args: SendArgs) => boolean;
}

export function useFunChallengesSocket(): FunChallengesSocketApi {
  const ctx = useContext(SocketContext);
  const socket = ctx?.socket ?? null;

  // Reactive readiness flag.
  // We keep the shared registry store as a *book-keeping* signal so consumers
  // see the moment a NEW socket gets schemas wired up — but we DON'T gate
  // `ready` on it, because the keyed `registerMessageHandler` path is itself
  // idempotent (the SDK replaces handlers by key). Earlier we strictly
  // required `registeredSocket === socket`, which left the Send button stuck
  // on "Connecting…" whenever the consumer rendered before the registry
  // store had caught up (race during fast-path mount, HMR-reset stores, or
  // a new socket after reconnect). The fix is to treat the presence of the
  // socket as ready, while still publishing to the registry for the
  // auto-rejoin / "is this a fresh socket?" code below.
  const registeredSocket = useFunChallengesRegistryStore(
    s => s.registeredSocket,
  );
  const setRegisteredSocket = useFunChallengesRegistryStore(
    s => s.setRegisteredSocket,
  );
  const ready = !!socket;

  const setRoom = useFunChallengesSessionStore(s => s.setRoom);
  const setRoomPlayers = useFunChallengesSessionStore(s => s.setRoomPlayers);
  const setRoomState = useFunChallengesSessionStore(s => s.setRoomState);
  const setSession = useFunChallengesSessionStore(s => s.setSession);
  const clearSession = useFunChallengesSessionStore(s => s.clearAll);

  const battleSetIncoming = useFunChallengesBattleStore(s => s.setIncomingChallenge);
  const battleStartInBattle = useFunChallengesBattleStore(s => s.startInBattle);
  const battleEnd = useFunChallengesBattleStore(s => s.endBattle);
  const battleSetError = useFunChallengesBattleStore(s => s.setError);
  const battleHydrate = useFunChallengesBattleStore(s => s.hydrateSession);
  const battleRecordOpponentMove = useFunChallengesBattleStore(
    s => s.recordOpponentMove,
  );

  // Resolved at call time so we always pick up the latest userId.
  // `loggedInUserstore.userId` is currently never populated (the chess
  // setUserId path lives in PlayChess/index.js and writes its own local
  // state). `userData.user_id` is the authoritative value set during
  // login (useLoginState → setUserData). Fall back to the store's userId
  // for safety if that path ever starts writing it.
  const myUserIdFromStore = useLoggedInUserStore(s => s.userId);
  const myUserIdFromUserData = useLoggedInUserStore(
    s => (s.userData && (s.userData.user_id ?? s.userData.pk)) || '',
  );
  const myUserId = String(
    myUserIdFromUserData || myUserIdFromStore || '',
  );

  // ── Inbound handlers ───────────────────────────────────────────────────
  const handlers = useMemo(() => {
    return {
      fun_room_created(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<{roomId: string; creatorId: string}>(
          data.payload,
        );
        if (!payload) return;
        // We're the creator — the lobby UI is already in `challenging` state
        // from `sendChallenge`. Record the roomId so we can cancel.
        const battle = useFunChallengesBattleStore.getState();
        if (battle.pendingChallenge && !battle.pendingChallenge.roomId) {
          useFunChallengesBattleStore.setState({
            pendingChallenge: {
              ...battle.pendingChallenge,
              roomId: payload.roomId,
            },
          });
        }
        // Mirror this room for the creator so the global listener's
        // auto-start effect (which gates on roomPlayersCount >= 2) operates
        // on data tied to THIS challenge, not whatever stale invite room
        // might still be sitting in the session store from a prior battle.
        setRoom({
          roomId: payload.roomId,
          creatorId: payload.creatorId,
          gameType: data.gameType,
          settings: battle.pendingChallenge?.settings ?? ({} as ChallengeSettings),
          state: 'waiting',
          players: [{userId: payload.creatorId, username: '', avatarUrl: ''}],
        });
      },

      fun_room_invite(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<{
          roomId: string;
          creatorId: string;
          creatorName: string;
          creatorAvatarUrl?: string;
          settings: ChallengeSettings;
        }>(data.payload);
        if (!payload) return;

        // All invites flow into the store normally; the listener decides
        // what to render. When the user is still on the result modal
        // (`status === 'ended'`), `setIncomingChallenge` preserves
        // `session`/`endResult` so BattleScreen keeps the result visible
        // behind the listener's accept/deny modal (see store action).

        const incoming: IncomingChallengePayload = {
          roomId: payload.roomId,
          battleType: asBattleType(data.gameType),
          challengerId: payload.creatorId,
          challengerName: payload.creatorName,
          challengerAvatarUrl: payload.creatorAvatarUrl,
          settings: payload.settings,
        };
        battleSetIncoming(incoming);
        setRoom({
          roomId: payload.roomId,
          creatorId: payload.creatorId,
          gameType: data.gameType,
          settings: payload.settings,
          state: 'waiting',
          players: [],
        });
      },

      fun_room_update(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<FunRoomUpdatePayload>(data.payload);
        if (!payload) return;
        if (payload.players) setRoomPlayers(payload.players);
        if (payload.state) setRoomState(payload.state);
      },

      fun_session_start(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<FunSessionStartPayload>(data.payload);
        if (!payload) return;

        setSession({
          sessionId: payload.sessionId,
          roomId: payload.roomId,
          gameType: data.gameType,
          playerIds: payload.playerIds,
          endsAt: parseInt(payload.endsAt, 10) || 0,
          settings: payload.settings,
          coords: payload.coords,
        });

        const opponentId =
          payload.playerIds.find(id => String(id) !== String(myUserId)) || '';

        // Best-effort opponentName resolution. The `fun_session_start`
        // payload only carries player IDs — names live in:
        //   - pendingChallenge   (the challenger picked them in the lobby)
        //   - incomingChallenge  (the accepter received them in the invite)
        //   - session-store room.players (mirrored from fun_room_update)
        // Try each in order so both sides of the battle render a real name
        // instead of "Opponent".
        const battleSnap = useFunChallengesBattleStore.getState();
        const sessionSnap = useFunChallengesSessionStore.getState();
        const pendingMatchesOpponent =
          battleSnap.pendingChallenge &&
          String(battleSnap.pendingChallenge.opponentId) === String(opponentId)
            ? battleSnap.pendingChallenge.opponentName
            : undefined;
        const incomingMatchesOpponent =
          battleSnap.incomingChallenge &&
          String(battleSnap.incomingChallenge.challengerId) ===
            String(opponentId)
            ? battleSnap.incomingChallenge.challengerName
            : undefined;
        const roomPlayerMatch = sessionSnap.room?.players?.find(
          p => String(p.userId) === String(opponentId),
        )?.username;
        const opponentName =
          (pendingMatchesOpponent && pendingMatchesOpponent.trim()) ||
          (incomingMatchesOpponent && incomingMatchesOpponent.trim()) ||
          (roomPlayerMatch && roomPlayerMatch.trim()) ||
          '';

        battleStartInBattle({
          battleId: payload.sessionId,
          battleType: asBattleType(data.gameType),
          myUserId: String(myUserId || ''),
          opponentId: String(opponentId),
          opponentName,
          endsAt: parseInt(payload.endsAt, 10) || 0,
          settings: payload.settings,
          coords: payload.coords,
        });
      },

      fun_move_broadcast(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<FunMoveBroadcastPayload>(data.payload);
        if (!payload) return;
        battleRecordOpponentMove(
          String(payload.playerId),
          payload.correct,
          payload.wrong,
          payload.nextIndex,
        );
      },

      fun_session_end(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<{
          sessionId: string;
          gameType: string;
          winnerId: string;
          rankedPlayers: string[];
          scores: Record<string, {correct: number; wrong: number}>;
          responses: Record<string, string[]>;
          reason: 'time_up' | 'forfeit' | 'mistake_out';
        }>(data.payload);
        if (!payload) return;
        battleEnd({
          battleId: payload.sessionId,
          battleType: asBattleType(data.gameType || payload.gameType),
          winnerId: payload.winnerId || '',
          rankedPlayers: payload.rankedPlayers || [],
          scores: payload.scores || {},
          responses: payload.responses || {},
          reason: payload.reason || 'time_up',
        });
        clearSession();
      },

      fun_session_rejoin(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<FunSessionRejoinPayload>(data.payload);
        if (!payload) return;

        const opponentId =
          payload.playerIds.find(id => String(id) !== String(myUserId)) || '';

        setSession({
          sessionId: payload.sessionId,
          roomId: payload.roomId,
          gameType: data.gameType,
          playerIds: payload.playerIds,
          endsAt: parseInt(payload.endsAt, 10) || 0,
          settings: payload.settings,
          coords: payload.coords || [],
        });

        const myScores = payload.scores?.[String(myUserId || '')] || {
          correct: 0,
          wrong: 0,
        };
        const oppScores = payload.scores?.[String(opponentId)] || {
          correct: 0,
          wrong: 0,
        };

        const rejoinOpponentName =
          payload.players?.find(
            p => String(p.userId) === String(opponentId),
          )?.username || '';

        // Reconstruct each player's traffic-light history row from the
        // server's per-player response trail.
        //
        // Two reconstruction strategies depending on the challenge type:
        //   - Square Trainer: response[i] is the square the player clicked;
        //     correctness is `response[i] === coords[i]` (the shared
        //     server-pushed sequence).
        //   - Position Trainer Battle + Puzzle Dash Battle: response[i] is
        //     the overloaded "<puzzleId>:<uci>:<0|1>" string the adapter
        //     emitted (one per terminal puzzle event). The trailing bit IS
        //     the correctness — we don't need to compare against `coords`.
        //     PDB uses the SAME adapter as PTB so the same suffix-parsing
        //     path is correct for both.
        const coordsArr = payload.coords || [];
        const incomingGameType =
          (data.gameType || '') ||
          (payload.settings?.battleType as string) ||
          '';
        const isPTBLikeWalker =
          incomingGameType === BattleType.POSITION_TRAINER ||
          incomingGameType === BattleType.PUZZLE_DASH;

        const reconstructMoves = (responses?: string[]): boolean[] => {
          if (!responses || responses.length === 0) return [];
          if (isPTBLikeWalker) {
            // Parse the trailing `:0|1` correctness bit. Anything malformed
            // is treated as a wrong move so the row stays the right length
            // and the adapter's mid-line state can be approximated from it.
            return responses.map(raw => {
              if (typeof raw !== 'string' || !raw) return false;
              const lastColon = raw.lastIndexOf(':');
              if (lastColon === -1) return false;
              const bit = raw.slice(lastColon + 1);
              return bit === '1' || bit === 'true';
            });
          }
          // Square Trainer: align response squares against coords by index.
          return responses.map((sq, i) => {
            const target = coordsArr[i % Math.max(1, coordsArr.length)] ?? '';
            return sq === target;
          });
        };
        const myMovesReconstructed = reconstructMoves(payload.myResponses);

        // Opponent reconstruction is preferred via `allResponses` (per-move
        // truth), but we fall back to an approximation built from cumulative
        // scores when the server omits the field — that keeps the row
        // non-empty for back-compat with older cc-game-service builds. The
        // approximation is `[true × correct, false × wrong]` — count is
        // correct, order is "all greens then all reds" instead of the real
        // interleave. Once any new `fun_move_broadcast` arrives the
        // idempotency guard keeps the row in sync from then on.
        const opponentResponses =
          payload.allResponses?.[String(opponentId)];
        let opponentMovesReconstructed: boolean[];
        if (opponentResponses && opponentResponses.length > 0) {
          opponentMovesReconstructed = reconstructMoves(opponentResponses);
        } else {
          if (!payload.allResponses) {
            console.warn(
              '[FunChallenges] rejoin payload missing allResponses — ' +
                'cc-game-service may need a restart to pick up the patch; ' +
                'opponent row will use an approximation from cumulative scores.',
            );
          }
          const c = oppScores.correct;
          const w = oppScores.wrong;
          opponentMovesReconstructed = [
            ...Array(c).fill(true),
            ...Array(w).fill(false),
          ];
        }

        battleHydrate(
          {
            battleId: payload.sessionId,
            battleType: asBattleType(data.gameType),
            myUserId: String(myUserId || ''),
            opponentId: String(opponentId),
            opponentName: rejoinOpponentName,
            endsAt: parseInt(payload.endsAt, 10) || 0,
            settings: payload.settings,
            coords: coordsArr,
          },
          {
            myCorrect: myScores.correct,
            myWrong: myScores.wrong,
            opponentCorrect: oppScores.correct,
            opponentWrong: oppScores.wrong,
          },
          payload.myResponses || [],
          {
            myMoves: myMovesReconstructed,
            opponentMoves: opponentMovesReconstructed,
          },
        );
      },

      fun_error(_socket: any, data: FunChallengeEnvelope) {
        const payload = parsePayload<FunChallengeError>(data.payload);
        if (!payload) return;
        // Silence the speculative-rejoin probe responses: when there's no
        // active session for this user the server replies with
        // NO_ACTIVE_SESSION / SESSION_ENDED / SESSION_NOT_FOUND. None of
        // these are real failures from the user's perspective — they just
        // mean "nothing to rejoin", which is the expected state for a
        // fresh login or post-game socket.
        if (
          data.requestId === REJOIN_PROBE_REQUEST_ID &&
          (payload.code === 'NO_ACTIVE_SESSION' ||
            payload.code === 'SESSION_ENDED' ||
            payload.code === 'SESSION_NOT_FOUND')
        ) {
          return;
        }
        battleSetError(payload);
      },
    };
  }, [
    battleSetIncoming,
    battleStartInBattle,
    battleEnd,
    battleSetError,
    battleHydrate,
    battleRecordOpponentMove,
    setRoom,
    setRoomPlayers,
    setRoomState,
    setSession,
    clearSession,
    myUserId,
  ]);

  // ── Schema registration — once per socket identity ────────────────────
  // The SDK's `registerMessageType` is idempotent on schema (it just
  // overwrites `schemaTypeDict[msgType]`), so re-running is safe — but we
  // also use the registry store to fire auto-rejoin exactly once per new
  // socket.
  useEffect(() => {
    if (!socket) return;

    if (!schema?.funChallengeEnvelopeSchema) {
      console.warn(
        '[FunChallenges] cc-client-sdk does not export funChallengeEnvelopeSchema — ' +
          'check the local install (npm install ../cc-client-sdk).',
      );
      return;
    }

    const envelope = schema.funChallengeEnvelopeSchema;
    const isNewSocket = registeredSocket !== socket;

    if (isNewSocket) {
      FUN_INBOUND_TYPES.forEach(msgType => {
        try {
          socket.registerMessageType(msgType, envelope, undefined);
        } catch (err) {
          console.warn(
            '[FunChallenges] inbound schema register failed for',
            msgType,
            err,
          );
        }
      });

      FUN_OUTBOUND_TYPES.forEach(msgType => {
        try {
          socket.registerMessageType(msgType, envelope, undefined);
        } catch (err) {
          console.warn(
            '[FunChallenges] outbound schema register failed for',
            msgType,
            err,
          );
        }
      });

      setRegisteredSocket(socket);

      // ── Always probe for an active session on a fresh socket ──────
      // The local Zustand store does NOT persist across hard refreshes,
      // so we can't gate this on `battle.status === 'in_battle'` like we
      // used to — that path skipped the probe on refresh and dumped the
      // user back on the lobby. The server's `getPlayerActiveSession`
      // (keyed on authenticated userId) is the canonical source of truth;
      // it replies with `fun_session_rejoin` (active session present) or
      // `fun_error: NO_ACTIVE_SESSION / SESSION_ENDED / SESSION_NOT_FOUND`.
      //
      // We tag the request with a recognizable `requestId` so the error
      // handler can suppress the NO_* / SESSION_* noise — those are
      // expected when there's nothing to rejoin, not a real failure.
      try {
        const battle = useFunChallengesBattleStore.getState();
        const probeRequestId = REJOIN_PROBE_REQUEST_ID;
        setTimeout(() => {
          try {
            if (typeof socket.send_msg === 'function') {
              socket.send_msg('fun_session_rejoin', {
                requestId: probeRequestId,
                type: 'fun_session_rejoin',
                gameType:
                  (battle.session?.battleType as string) ||
                  'fun_square_trainer',
                id: '',
                payload: '{}',
              });
            }
          } catch (err) {
            console.warn('[FunChallenges] auto-rejoin send failed', err);
          }
        }, 800);
      } catch {
        // Best-effort — never block the registration path.
      }
    }
  }, [socket, registeredSocket, setRegisteredSocket]);

  // 2026-05-21 bug 3: re-fire the rejoin probe whenever `myUserId`
  // transitions from empty → non-empty. After a hard refresh:
  //   1. Socket reconnects (identity-change effect above fires the FIRST
  //      probe at ~800ms). At that moment `useLoggedInUserStore.userData`
  //      may still be null because the login API hasn't returned yet, so
  //      the socket might not even be authenticated server-side and the
  //      probe gets NO_ACTIVE_SESSION (silently suppressed).
  //   2. Login API returns; userData populates; `myUserId` flips to a
  //      real value. We fire a SECOND probe so the server gets a chance
  //      to return our active session.
  //
  // The probe is idempotent server-side (read-only `getPlayerActiveSession`),
  // and the client's `fun_session_rejoin` handler is idempotent too
  // (setSession + hydrate just overwrite). Worst case the user pays one
  // extra socket roundtrip.
  const prevMyUserIdRef = useRef<string>('');
  useEffect(() => {
    if (!socket || typeof socket.send_msg !== 'function') return;
    const prev = prevMyUserIdRef.current;
    prevMyUserIdRef.current = myUserId;
    if (prev) return;            // we already had an id; not the transition we care about
    if (!myUserId) return;       // still empty
    try {
      socket.send_msg('fun_session_rejoin', {
        requestId: REJOIN_PROBE_REQUEST_ID,
        type: 'fun_session_rejoin',
        gameType: 'fun_square_trainer',
        id: '',
        payload: '{}',
      });
    } catch (err) {
      console.warn('[FunChallenges] auto-rejoin (myUserId resolved) failed', err);
    }
  }, [socket, myUserId]);

  // ── Handler (re-)installation — runs whenever the handlers closure
  // changes (e.g. after `myUserId` resolves) so the latest closure
  // replaces the previously-keyed one. The SDK's keyed registry replaces
  // by `handlerKey`, so this is idempotent and won't duplicate listeners.
  useEffect(() => {
    if (!socket) return;
    if (typeof socket.registerMessageHandler !== 'function') return;
    FUN_INBOUND_TYPES.forEach(msgType => {
      try {
        socket.registerMessageHandler(
          msgType,
          handlers[msgType],
          HANDLER_PREFIX + msgType,
        );
      } catch (err) {
        console.warn('[FunChallenges] handler attach failed for', msgType, err);
      }
    });
  }, [socket, handlers]);

  // ── Outbound helper ───────────────────────────────────────────────────
  const api = useMemo<FunChallengesSocketApi>(() => {
    return {
      socket,
      ready,
      send: ({type, gameType, id = '', payload = {}, requestId = ''}: SendArgs) => {
        if (!socket || typeof socket.send_msg !== 'function') return false;
        try {
          socket.send_msg(type, {
            requestId: requestId || String(Date.now()),
            type,
            gameType: String(gameType || ''),
            id,
            payload: JSON.stringify(payload),
          });
          return true;
        } catch (err) {
          console.warn('[FunChallenges] send failed for', type, err);
          return false;
        }
      },
    };
  }, [socket, ready]);

  return api;
}
